import { useEffect, useRef, useState } from "react";

import {
  useLazyGetCopilotExportJobStatusQuery,
  useLazyGetCopilotExportJobDownloadQuery,
} from "state/rtkQuery/copilot/copilotQueries";
import { showLoadingToast, showToast } from "utils/reusableToast";
import { saveExportJob, loadExportJob, clearExportJob } from "utils/exportJobStorage";
import { capitalizeFirstLetter } from "utils/capitalizeString";

const POLL_INTERVAL_MS = 1500;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The shared export-job endpoints answer with specific statuses; say what each one means to the user.
export const EXPORT_ERROR_MESSAGES = {
  403: "You don't have permission to access this export.",
  404: "This export is unavailable or you can't access it.",
  409: "The export file isn't ready yet. Please try again shortly.",
  410: "This export has expired. Please start a new one.",
};
export const exportErrorMessage = (error, fallback) => EXPORT_ERROR_MESSAGES[error?.status] ?? error?.data?.detail ?? fallback;

// Short human-readable summary of what's in the export — shown in the toast
// instead of the backend's UUID-based filename (e.g.
// copilot_usage_e62ab...4163527.csv), which means nothing to the user. The
// actual downloaded file still keeps the backend's filename, since renaming
// it would require fetching the signed S3 URL as a blob, which needs the
// bucket to allow CORS for this origin — not guaranteed, so we stick with a
// direct window.open() navigation (no CORS needed) for the download itself.
const buildExportSummary = ({ exportScopeName, queryFrom, queryTo }) => `${exportScopeName} · ${queryFrom} to ${queryTo}`;

// Drives the async Copilot export job flow (start -> poll -> download) shared
// by every dashboard that offers a Copilot usage/budget export — org-admin,
// enterprise-wide, and enterprise cost-center-scoped. Job status/download are
// the same endpoints regardless of how or where the job was started; only
// the job-start call itself differs per dashboard/export type/scope, so that
// part stays the caller's responsibility (passed in as startJob).
//
// namespace disambiguates this dashboard's persisted export jobs (see
// utils/exportJobStorage) from every other dashboard's — e.g. "admin" vs
// "enterprise" — so an in-flight job on one dashboard is never picked up by
// another, including across browser tabs.
export const useCopilotExportJob = (namespace) => {
  const [triggerExportJobStatus] = useLazyGetCopilotExportJobStatusQuery();
  const [triggerExportJobDownload] = useLazyGetCopilotExportJobDownloadQuery();
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingBudget, setIsExportingBudget] = useState(false);
  const [isExportingMetered, setIsExportingMetered] = useState(false);

  const TRACKER_KEYS = { usage: `${namespace}-usage`, budget: `${namespace}-budget`, metered: `${namespace}-metered` };

  // Drives one export job to completion: polls status until "completed" (or
  // "failed"), keeping a single toast (toastId) up to date with progress the
  // whole time, then resolves the finished job status.
  const pollExportJob = async ({ jobId, toastId, label }) => {
    for (;;) {
      const { data: jobStatus, error } = await triggerExportJobStatus({ jobId });
      if (error || !jobStatus) {
        throw new Error(exportErrorMessage(error, `Failed to check ${label} export status.`));
      }
      if (jobStatus.status === "completed") {
        return jobStatus;
      }
      if (jobStatus.status === "failed") {
        throw new Error(jobStatus.error_message ?? `${label} export failed.`);
      }
      if (jobStatus.status === "expired") {
        throw new Error(EXPORT_ERROR_MESSAGES[410]);
      }
      if (jobStatus.status === "canceled") {
        throw new Error(`${capitalizeFirstLetter(label)} export was canceled.`);
      }
      const pct = Math.round(jobStatus.progress_percent ?? 0);
      showLoadingToast({
        id: toastId,
        title: `Exporting ${label}… ${pct}%`,
        description: "Please don't close or navigate away from this page.",
      });
      await sleep(POLL_INTERVAL_MS);
    }
  };

  // Opens the finished export's download in a new tab and shows the
  // "ready" toast. Called both from the live in-tab flow (auto-opens) and
  // from the resume-on-mount flow (opens only on click, since browsers block
  // window.open() outside of a user gesture).
  const finishExportJob = async ({ jobId, toastId, label, exportScopeName, queryFrom, queryTo, autoOpen }) => {
    showLoadingToast({ id: toastId, title: `Preparing ${label} download…`, description: "Please don't close or navigate away from this page." });
    const { data: downloadInfo, error: downloadError } = await triggerExportJobDownload({ jobId });
    if (downloadError || !downloadInfo?.download_url) {
      throw new Error(exportErrorMessage(downloadError, `Failed to get ${label} download link.`));
    }
    const summary = buildExportSummary({ exportScopeName, queryFrom, queryTo });

    if (autoOpen) {
      window.open(downloadInfo.download_url, "_blank", "noopener,noreferrer");
      showToast({ id: toastId, type: "success", title: `${capitalizeFirstLetter(label)} export ready`, description: summary });
    } else {
      // Job finished while the user was away (e.g. after a refresh) — can't
      // auto-open a new tab without a fresh click, so surface a persistent,
      // clickable toast instead. The action ("click here") goes in the title,
      // not buried in the description, since the title is the one line a
      // user skimming a toast is guaranteed to read before dismissing it.
      showToast({
        id: toastId,
        type: "success",
        title: `${capitalizeFirstLetter(label)} export ready — click here to download`,
        description: summary,
        duration: Infinity,
        onClick: () => window.open(downloadInfo.download_url, "_blank", "noopener,noreferrer"),
      });
    }
  };

  const runExportJob = async ({ label, toastId, trackerKey, startJob, setIsExporting: setBusy, exportScopeName, queryFrom, queryTo }) => {
    setBusy(true);
    showLoadingToast({ id: toastId, title: `Starting ${label} export…`, description: "Please don't close or navigate away from this page." });
    try {
      const { job_id: jobId } = await startJob();
      saveExportJob(trackerKey, { jobId, label, toastId, fromDate: queryFrom, toDate: queryTo, exportScopeName });

      await pollExportJob({ jobId, toastId, label });
      await finishExportJob({ jobId, toastId, label, exportScopeName, queryFrom, queryTo, autoOpen: true });
      clearExportJob(trackerKey);
    } catch (err) {
      showToast({
        id: toastId,
        type: "error",
        title: `${capitalizeFirstLetter(label)} export failed`,
        description: err?.message ?? "Please try again later.",
      });
      clearExportJob(trackerKey);
    } finally {
      setBusy(false);
    }
  };

  // handleExport/handleBudgetExport are built per-call by the caller (via
  // makeHandlers below) since the two dashboards construct their startJob
  // params differently (enterprise branches on org/cost-center/enterprise-wide
  // scope; org-admin always has a fixed orgId).
  // Metered usage exports ride on the same lifecycle: only startMeteredJob (job creation) differs.
  const makeHandlers = ({ platformId, exportScopeName, queryFrom, queryTo, startUsageJob, startBudgetJob, startMeteredJob }) => ({
    handleMeteredExport: () => {
      if (!platformId || !startMeteredJob) return;
      return runExportJob({
        label: "metered usage",
        toastId: `${namespace}-metered-export`,
        trackerKey: TRACKER_KEYS.metered,
        startJob: startMeteredJob,
        setIsExporting: setIsExportingMetered,
        exportScopeName,
        queryFrom,
        queryTo,
      });
    },
    handleExport: () => {
      if (!platformId) return;
      return runExportJob({
        label: "usage",
        toastId: `${namespace}-usage-export`,
        trackerKey: TRACKER_KEYS.usage,
        startJob: startUsageJob,
        setIsExporting,
        exportScopeName,
        queryFrom,
        queryTo,
      });
    },
    handleBudgetExport: () => {
      if (!platformId) return;
      return runExportJob({
        label: "budget",
        toastId: `${namespace}-budget-export`,
        trackerKey: TRACKER_KEYS.budget,
        startJob: startBudgetJob,
        setIsExporting: setIsExportingBudget,
        exportScopeName,
        queryFrom,
        queryTo,
      });
    },
  });

  // Resume-on-mount — reconnects to any job that was in flight before a
  // refresh/reopen. Runs once; the ref guards React 18 StrictMode's
  // double-invoke of effects in development so we don't reattach twice.
  // Checks every tracker key since usage, budget and metered exports are independent.
  const hasResumedRef = useRef(false);
  useEffect(() => {
    if (hasResumedRef.current) return;
    hasResumedRef.current = true;

    const resumeJob = async (trackerKey, setBusy) => {
      const job = loadExportJob(trackerKey);
      if (!job) return;

      setBusy(true);
      showLoadingToast({ id: job.toastId, title: `Resuming ${job.label} export…`, description: "Please don't close or navigate away from this page." });

      try {
        await pollExportJob({ jobId: job.jobId, toastId: job.toastId, label: job.label });
        await finishExportJob({
          jobId: job.jobId,
          toastId: job.toastId,
          label: job.label,
          exportScopeName: job.exportScopeName,
          queryFrom: job.fromDate,
          queryTo: job.toDate,
          autoOpen: false,
        });
      } catch (err) {
        showToast({
          id: job.toastId,
          type: "error",
          title: `${capitalizeFirstLetter(job.label)} export failed`,
          description: err?.message ?? "Please try again later.",
        });
      } finally {
        setBusy(false);
        clearExportJob(trackerKey);
      }
    };

    resumeJob(TRACKER_KEYS.usage, setIsExporting);
    resumeJob(TRACKER_KEYS.budget, setIsExportingBudget);
    resumeJob(TRACKER_KEYS.metered, setIsExportingMetered);
    // Resume-on-mount only needs to run once on initial render.
  }, []);

  return { isExporting, isExportingBudget, isExportingMetered, makeHandlers };
};
